import React, { useEffect, useRef } from 'react';
import maplibre from 'maplibre-gl';
import GeoJSON from 'geojson'
import 'maplibre-gl/dist/maplibre-gl.css';

const buildGeoJSON = (dataArray) => {
  const finalDataArray = (dataArray || []).map((item) => {
    const lat = item?.scan_metadata?.latitude;
    const long = item?.scan_metadata?.longitude;

    return {
      crop: `${item.crop_name} (${item.variety_name})`,
      machine: `${item.machine_name} (${item.machine_code})`,
      scan_id: `${item.scan_id}`,
      lat: lat,
      long: long,
      overall_score: item.overall_score
    }
  }).filter((item) => item.lat && item.long)

  return GeoJSON.parse(finalDataArray, { Point: ['lat', 'long'] })
}

const MapComponent = ({ data, dataArray, height = 420 }) => {
  const mapRef = useRef(null)
  const containerRef = useRef(null)

  // Create the map exactly once
  useEffect(() => {
    if (mapRef.current) return // guard: never create a second instance

    const apiKey = "ZOrHkYad4ocDPfZvaMNV"

    const map = new maplibre.Map({
      container: containerRef.current,
      style: 'https://api.maptiler.com/maps/basic/style.json?key=' + apiKey,
      center: [78.9629, 23.5937],
      zoom: 4
    });
    mapRef.current = map

    map.addControl(new maplibre.NavigationControl(), 'top-left');
    map.scrollZoom.disable();

    map.on('load', () => {
      map.addSource('finalData', {
        type: 'geojson',
        data: buildGeoJSON(dataArray),
        cluster: true,
        clusterMaxZoom: 15,
        clusterRadius: 50
      });

      map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'finalData',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step', ['get', 'point_count'],
            '#34648f', 100, '#d96c3c', 750, '#63b364'
          ],
          'circle-radius': [
            'step', ['get', 'point_count'],
            20, 100, 30, 750, 40
          ]
        }
      });

      map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'finalData',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12
        },
        paint: { 'text-color': '#ffffff' }
      });

      map.addLayer({
        id: 'unclustered-point',
        type: 'circle',
        source: 'finalData',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': '#22638f',
          'circle-radius': 5,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#fff'
        }
      });

      map.on('click', 'clusters', (e) => {
        const features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
        const clusterId = features[0].properties.cluster_id;
        map.getSource('finalData').getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;
          map.easeTo({ center: features[0].geometry.coordinates, zoom });
        });
      });

      map.on('click', 'unclustered-point', (e) => {
        const coordinates = e.features[0].geometry.coordinates.slice();
        const { crop, machine, scan_id, overall_score } = e.features[0].properties;

        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        new maplibre.Popup()
          .setLngLat(coordinates)
          .setHTML(
            `<div style="font-size: 13px; line-height: 1.6;">
              <div><b>Crop:</b> ${crop}</div>
              <div><b>Machine:</b> ${machine}</div>
              <div><b>Scan ID:</b> ${scan_id}</div>
              <div><b>Overall Score:</b> ${overall_score}</div>
            </div>`
          )
          .addTo(map);
      });

      map.on('mouseenter', 'clusters', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'clusters', () => { map.getCanvas().style.cursor = ''; });
    });

    return () => {
      map.remove()
      mapRef.current = null
    }
  }, []) // <-- empty deps: runs only on mount/unmount, never recreates the map

  // Update the existing map's data whenever dataArray changes — no new context created
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    const updateSource = () => {
      const source = map.getSource('finalData')
      if (source) source.setData(buildGeoJSON(dataArray))
    }

    if (map.isStyleLoaded()) {
      updateSource()
    } else {
      map.once('load', updateSource)
    }
  }, [dataArray])

  return (
    <div
      ref={containerRef}
      style={{
        height,
        width: '100%',
        borderRadius: '14px',
        overflow: 'hidden',
      }}
    />
  );
};

export default MapComponent;